<template>
    <div>
        <template v-if="reservationLoading">
            <Loading :show="reservationLoading" />
        </template>
        <template v-else>
            <div class="container-fluid py-2" style="min-height: 500px">
                <div class="row border-bottom pb-3 mb-4">
                    <div class="col-md-6 offset-md-3">
                        <ValidationProvider name="date" rules="" v-slot="{ valid, errors }">
                            <b-form-group :label="$t('reservation_date')">
                                <select-date
                                    v-model="selectedDate"
                                    @input="fetchScheduleForDate"
                                    :restrictDays="7"
                                    :state="errors[0] ? false : null"
                                    :show-today-button="true"
                                />
                            </b-form-group>
                        </ValidationProvider>
                    </div>
                </div>
                <transition name="fade" mode="out-in">
                    <!-- Resmi Tatil Durum Kartı -->
                    <div v-if="isSelectedDateHoliday" key="holiday-state" class="text-center py-5 my-4 holiday-alert-box">
                        <div class="holiday-alert-card p-5 shadow-sm border-0 rounded-lg">
                            <div class="icon-pulse-wrapper mb-4">
                                <i class="ri-calendar-close-line text-danger ri-5x"></i>
                            </div>
                            <h4 class="text-danger font-weight-bold mb-3">
                                {{ $t("library_closed_holiday") }}
                            </h4>
                            <p class="text-muted mx-auto explanation-text" style="max-width: 600px;">
                                <span v-html="$t('library_closed_holiday_desc', {
                                    date: `<span class='font-weight-bold text-dark'>${toLocaleDate(selectedDate)}</span>`,
                                    holiday: selectedDateHolidayInfo ? `<span class='badge badge-soft-danger px-2 py-1 font-size-14 font-weight-bold mx-1'>${selectedDateHolidayInfo.explanation}</span>` : ''
                                })"></span>
                            </p>
                            <p class="text-secondary font-size-14 mt-4 mb-0">
                                <i class="ri-information-line align-middle"></i> {{ $t("please_select_another_date_for_reservation") }}
                            </p>
                        </div>
                    </div>

                    <div v-else-if="Object.keys(timeSlotsData).length" key="chair-grid" class="mt-4">
                        <div class="d-flex justify-content-end mb-4">
                            <div>
                                <b-badge variant="success" class="mr-2 px-3 py-2">
                                    <i class="ri-check-line"></i> {{ $t("available_chairs") }}
                                </b-badge>
                                <b-badge variant="danger" class="px-3 py-2">
                                    <i class="ri-close-line"></i> {{ $t("occupied") }}
                                </b-badge>
                                <b-badge variant="primary" class="px-3 py-2 mt-2 mt-lg-0 ml-0 ml-lg-2">
                                    <i class="ri-reserved-line"></i> {{ $t("your_reservation") }}
                                </b-badge>
                            </div>
                        </div>

                        <div
                            v-for="(chairs, timeslot) in timeSlotsData"
                            :key="timeslot"
                            class="mb-5 border rounded p-4 shadow-sm bg-white"
                        >
                            <h5 class="text-primary font-weight-bold border-bottom pb-3 mb-4">
                                <i class="ri-time-line"></i>
                                {{ toLocaleDate(selectedDate) }} {{ timeslot }}
                                {{ $t("session_seating_plan") }}
                            </h5>

                            <div class="simple-seat-grid pb-2 custom-scrollbar">
                                <div
                                    v-for="(chair, key) in chairs"
                                    :key="chair.name"
                                    class="seat-item shadow-sm"
                                    :class="
                                             (chair.have_reservation && chair.is_your_reservation) || chair.is_your_reservation
                                            ? 'seat-your-reservation'
                                            : chair.have_reservation
                                            ? 'seat-occupied'
                                            : 'seat-available'
                                    "
                                    @click="makeReservation(chair, timeslot)"
                                >

                                    <i class="ri-parent-line seat-icon"></i>
                                    <div class="seat-name">{{ chair.name }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-else-if="Object.keys(timeSlotsData).length === 0"
                        key="empty-state"
                        class="text-center py-5 text-muted"
                    >
                        <i class="ri-calendar-event-line ri-4x mb-3 text-light"></i>
                        <h5>{{ $t("no_session_found_for_date") }}</h5>
                    </div>
                </transition>
            </div>
        </template>
    </div>
</template>

<script>
import SelectDate from "@/components/interactive-fields/SelectDate";
import moment from "moment";
import qs from "qs";
import LibraryReservationService from "@/services/LibraryReservationService";
import HolidayServices from "@/services/HolidayServices";

export default {
    name: "Chairs",
    components: {
        SelectDate,
    },
    props: {
        selectedRoom: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            reservationLoading: false,
            selectedDate: moment().format("YYYY-MM-DD"),
            timeSlotsData: {},
            isTodayHoliday: false,
            holidays: [],
            holidaysList: []
        };
    },
    computed: {
        user() {
            return this.$store.getters["auth/getUser"];
        },
        isStudent() {
            return this.$store.getters["auth/isStudent"];
        },
        isSelectedDateHoliday() {
            return this.holidays.includes(this.selectedDate);
        },
        selectedDateHolidayInfo() {
            if (!this.holidaysList) return null;
            return this.holidaysList.find(h => h.date === this.selectedDate) || null;
        },
    },
    mounted() {
        this.fetchScheduleForDate();
    },
    created() {
      this.getHolidays()
    },
    methods: {
        async getHolidays() {
          try {
              const config={
                  params:{
                      limit:-1
                  },
                  paramsSerializer: (params) => qs.stringify(params, { encode: false }),
              }
              const res = await LibraryReservationService.libraryHolidays(config)
              const data = res.data.data
              this.holidaysList = data || [];
              this.holidays = data?.map((item)=>item.date) || [];
          }catch (err) {
              this.showErrors(err, null);
          }
        },
        async fetchScheduleForDate() {
            if (!this.selectedRoom) return;
            if (this.isSelectedDateHoliday) {
                this.timeSlotsData = {};
                return;
            }
            try {
                this.reservationLoading = true;
                const filter = {
                    params: {
                        filter: {
                            library_room_id: this.selectedRoom.id,
                            date: this.selectedDate,
                        },
                    },
                    paramsSerializer: (params) =>
                        qs.stringify(params, { encode: false }),
                };
                const res = await LibraryReservationService.getReservations(filter);
                this.timeSlotsData = res.data.data;
                const slots = res.data.data[0]?.slots;
                this.setSlots(slots);
                this.$emit('close-bottom-modal')
            } catch (err) {
                this.showErrors(err, null);
            } finally {
                this.reservationLoading = false;
            }
        },
        makeReservation(chair, timeslot) {
            if (chair.is_your_reservation) {
                const reservation = this.timeSlotsData[timeslot
                    ]?.find((r) => r.chair_id === chair.chair_id);
                if (reservation) {
                    this.$emit("reservation-selected", {
                        ...reservation,
                        chair_name: chair.name,
                    });
                }
                return;
            }
            if (chair.have_reservation) {
                this.$toast.error(this.$t("this_chair_is_occupied"));
                return;
            }

            let readableDate = moment(this.selectedDate).format("DD.MM.YYYY");
            this.$swal({
                title: this.$t("reservation_confirm"),
                html: `<b>${readableDate}</b> ${this.$t("on_date")} <b>${timeslot}</b> ${this.$t("in_session")} <br><br><b>${chair.name}</b> ${this.$t("reserve_chair_number")}`,
                type: "question",
                showCancelButton: true,
                confirmButtonText: this.$t("yes_reserve_for_me"),
                cancelButtonText: this.$t("cancel"),
                confirmButtonColor: "#28a745",
            }).then(async (result) => {
                if (result.value) {
                    const start_time = timeslot.split("-")[0];
                    const end_time = timeslot.split("-")[1];
                    try {
                        const data = {
                            date: this.selectedDate,
                            start_time: start_time,
                            library_chair_id: chair.chair_id,
                            end_time: end_time,
                        };
                        const res = await LibraryReservationService.saveReservation(data);
                        this.$toast.success(this.$t("api." + res.data.message));
                        await this.fetchScheduleForDate();
                        this.$emit("refresh-reservations");
                    } catch (err) {
                        this.showErrors(err, null);
                    }
                }
            });
        },
        setSlots(slots) {
            if (slots && !slots.length) {
                this.timeSlotsData = {};
            }
            const newObj = {};
            slots.forEach((slot) => {
                newObj[slot.start_time + "-" + slot.end_time] = [];
            });
            this.timeSlotsData.forEach((item) => {
                item.slots.forEach((slot) => {
                    newObj[slot.start_time + "-" + slot.end_time].push({
                        name: item.chair_name,
                        chair_id: item.chair_id,
                        have_reservation: slot.have_reservation,
                        is_your_reservation: slot.is_your_reservation,
                        date: this.selectedDate,
                        end_time:slot.end_time,
                        reservation_id:slot.reservation_id,
                        start_time:slot.start_time
                    });
                });
            });
            this.timeSlotsData = newObj;
        },
    },
};
</script>

<style scoped>
/* Custom Scrollbar for Time Slots */
.custom-scrollbar::-webkit-scrollbar {
    height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Seat Grid */
.simple-seat-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    max-height: 350px;
    overflow-y: auto;
}

/* Seat Item styling */
.seat-item {
    width: 65px;
    height: 65px;
    border-radius: 8px 8px 15px 15px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    position: relative;
    color: white;
}

/* Armrests Simulation */
.seat-item::after {
    content: "";
    position: absolute;
    bottom: -5px;
    width: 45px;
    height: 10px;
    background: inherit;
    filter: brightness(0.9);
    border-radius: 4px;
    z-index: 1;
}

.seat-icon {
    font-size: 24px;
    margin-bottom: -5px;
    z-index: 2;
}
.seat-name {
    font-size: 13px;
    font-weight: 700;
    z-index: 2;
}

/* Seat Status Colors */
.seat-available {
    background-color: #2ecfa2;
    border: 2px solid #23a07d;
}
.seat-available:hover {
    background-color: #25b38a;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(46, 207, 162, 0.4);
}

.seat-occupied {
    background-color: #f15656;
    border: 2px solid #ce4040;
    cursor: not-allowed;
    opacity: 0.8;
}

.seat-your-reservation {
    background-color: #0d6efd;
    border: 2px solid #0d6efd;
    cursor: pointer;
    opacity: 0.9;
}
.seat-your-reservation:hover {
    background-color: #0b5ed7;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(13, 110, 253, 0.4);
}

/* Resmi Tatil Kartı Premium Tasarım Arayüzü */
.holiday-alert-card {
    background: #fff5f5;
    border: 1px solid #ffd8d8 !important;
    border-radius: 16px;
    max-width: 750px;
    margin: 0 auto;
    transition: all 0.3s ease;
}

.holiday-alert-card:hover {
    box-shadow: 0 10px 25px rgba(241, 86, 86, 0.1) !important;
    transform: translateY(-2px);
}

.icon-pulse-wrapper {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 90px;
    height: 90px;
    background: #ffebeb;
    border-radius: 50%;
}

.badge-soft-danger {
    color: #f15656;
    background-color: #ffebeb;
    border: 1px solid #ffd8d8;
    border-radius: 6px;
}

.explanation-text {
    font-size: 16px;
    line-height: 1.6;
}
</style>
